const STATUS_BADGE_TONES = {
  slate:
    "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-700/60 dark:bg-slate-900/40 dark:text-slate-200",
  zinc: "border-zinc-300 bg-zinc-100 text-zinc-900 dark:border-zinc-700/60 dark:bg-zinc-900/40 dark:text-zinc-200",
  red: "border-red-300 bg-red-100 text-red-900 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-200",
  yellow:
    "border-yellow-300 bg-yellow-100 text-yellow-900 dark:border-yellow-800/60 dark:bg-yellow-950/40 dark:text-yellow-200",
  amber:
    "border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200",
  emerald:
    "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200",
  sky: "border-sky-300 bg-sky-100 text-sky-900 dark:border-sky-800/60 dark:bg-sky-950/40 dark:text-sky-200",
  blue: "border-blue-300 bg-blue-100 text-blue-900 dark:border-blue-800/60 dark:bg-blue-950/40 dark:text-blue-200",
  indigo:
    "border-indigo-300 bg-indigo-100 text-indigo-900 dark:border-indigo-800/60 dark:bg-indigo-950/40 dark:text-indigo-200",
  violet:
    "border-violet-300 bg-violet-100 text-violet-900 dark:border-violet-800/60 dark:bg-violet-950/40 dark:text-violet-200",
  cyan: "border-cyan-300 bg-cyan-100 text-cyan-900 dark:border-cyan-800/60 dark:bg-cyan-950/40 dark:text-cyan-200",
} as const;

type StatusTone = keyof typeof STATUS_BADGE_TONES;

type StatusToneMap = Record<string, StatusTone>;

function normalizeStatus(value: unknown): string {
  if (typeof value !== "string") return "";
  return value
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function pickStatusTone(
  normalizedStatus: string,
  toneMap: StatusToneMap,
  defaultTone: StatusTone,
): string {
  if (!normalizedStatus) return STATUS_BADGE_TONES.slate;

  const exactTone = toneMap[normalizedStatus];
  if (exactTone) return STATUS_BADGE_TONES[exactTone];

  if (normalizedStatus.includes("fail") || normalizedStatus.includes("reject")) {
    return STATUS_BADGE_TONES.red;
  }
  if (normalizedStatus.includes("cancel")) {
    return STATUS_BADGE_TONES.zinc;
  }
  if (normalizedStatus.includes("hold")) {
    return STATUS_BADGE_TONES.yellow;
  }
  if (normalizedStatus.includes("spot")) {
    return STATUS_BADGE_TONES.cyan;
  }
  if (
    normalizedStatus.includes("deliver") ||
    normalizedStatus.includes("paid") ||
    normalizedStatus.includes("success")
  ) {
    return STATUS_BADGE_TONES.emerald;
  }
  if (normalizedStatus.includes("pending")) {
    return STATUS_BADGE_TONES.yellow;
  }
  if (normalizedStatus.includes("transit")) {
    return STATUS_BADGE_TONES.violet;
  }
  if (normalizedStatus.includes("ready")) {
    return STATUS_BADGE_TONES.sky;
  }

  return STATUS_BADGE_TONES[defaultTone];
}

const ORDER_STATUS_TONES: StatusToneMap = {
  "checkout initiated": "yellow",
  "pending payment": "yellow",
  "payment processing": "yellow",
  pending: "yellow",
  "awaiting inventory": "amber",
  "ready to prepare": "sky",
  "ready for pickup": "sky",
  confirmed: "sky",
  "in transit": "violet",
  delivered: "emerald",
  "on hold": "yellow",
  cancelled: "zinc",
  canceled: "zinc",
};

const PAYMENT_STATUS_TONES: StatusToneMap = {
  paid: "emerald",
  succeeded: "emerald",
  completed: "emerald",
  pending: "yellow",
  unpaid: "yellow",
  processing: "yellow",
  partial: "blue",
  overdue: "red",
  failed: "red",
  defaulted: "red",
  expired: "red",
  cancelled: "zinc",
  canceled: "zinc",
  refunded: "zinc",
};

const INVENTORY_STATUS_TONES: StatusToneMap = {
  available: "emerald",
  active: "emerald",
  reserved: "sky",
  allocated: "sky",
  "in spot": "cyan",
  spot: "cyan",
  "in transit": "violet",
  shipping: "violet",
  "at origin": "sky",
  upcoming: "yellow",
  "sold out": "zinc",
  depleted: "zinc",
  archived: "zinc",
};

const CONTRACT_STATUS_TONES: StatusToneMap = {
  draft: "sky",
  pending: "yellow",
  approved: "blue",
  sent: "indigo",
  signed: "indigo",
  active: "emerald",
  completed: "zinc",
  fulfilled: "zinc",
  cancelled: "red",
  canceled: "red",
  rejected: "red",
  expired: "red",
};

const SAMPLE_STATUS_TONES: StatusToneMap = {
  requested: "yellow",
  pending: "yellow",
  approved: "blue",
  shipped: "violet",
  delivered: "emerald",
  rejected: "red",
  cancelled: "red",
  canceled: "red",
};

const RESERVATION_STATUS_TONES: StatusToneMap = {
  active: "blue",
  expired: "red",
  released: "zinc",
  converted: "emerald",
};

const PLAN_STATUS_TONES: StatusToneMap = {
  active: "emerald",
  completed: "blue",
  defaulted: "red",
  cancelled: "zinc",
  canceled: "zinc",
  pending: "yellow",
};

const SHIPPING_ADJUSTMENT_STATUS_TONES: StatusToneMap = {
  paid: "emerald",
  pending: "yellow",
  expired: "red",
  cancelled: "zinc",
  canceled: "zinc",
};

export function getOrderStatusBadgeClass(status: unknown): string {
  return pickStatusTone(normalizeStatus(status), ORDER_STATUS_TONES, "slate");
}

export function getPaymentStatusBadgeClass(status: unknown): string {
  return pickStatusTone(normalizeStatus(status), PAYMENT_STATUS_TONES, "slate");
}

export function getInventoryStatusBadgeClass(status: unknown): string {
  return pickStatusTone(normalizeStatus(status), INVENTORY_STATUS_TONES, "slate");
}

export function getContractStatusBadgeClass(status: unknown): string {
  return pickStatusTone(normalizeStatus(status), CONTRACT_STATUS_TONES, "slate");
}

export function getSampleStatusBadgeClass(status: unknown): string {
  return pickStatusTone(normalizeStatus(status), SAMPLE_STATUS_TONES, "slate");
}

export function getReservationStatusBadgeClass(status: unknown): string {
  return pickStatusTone(normalizeStatus(status), RESERVATION_STATUS_TONES, "slate");
}

export function getPlanStatusBadgeClass(status: unknown): string {
  return pickStatusTone(normalizeStatus(status), PLAN_STATUS_TONES, "slate");
}

export function getShippingAdjustmentStatusBadgeClass(status: unknown): string {
  return pickStatusTone(
    normalizeStatus(status),
    SHIPPING_ADJUSTMENT_STATUS_TONES,
    "slate",
  );
}

/**
 * Get badge variant for shipping adjustment effective status
 * Used by the new Effect-based shipping adjustment workflow
 */
export function getStatusBadgeVariant(
  status: string,
): "default" | "secondary" | "destructive" | "outline" {
  const normalized = normalizeStatus(status);

  if (
    normalized.includes("paid") ||
    normalized.includes("collected") ||
    normalized.includes("resolved") ||
    normalized.includes("credited")
  ) {
    return "default";
  }

  if (normalized.includes("awaiting") || normalized.includes("due")) {
    return "secondary";
  }

  if (
    normalized.includes("review") ||
    normalized.includes("expired") ||
    normalized.includes("failed") ||
    normalized.includes("error")
  ) {
    return "destructive";
  }

  if (normalized.includes("installment")) {
    return "default";
  }

  return "outline";
}
